import {
    Box,
    CircularProgress,
    IconButton,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography
} from "@mui/material"
import {
    DownloadIcon,
    InfoIconOutlined
} from "../assets/SvgIcons"
import CustomTable from "./investorOverview/common/customTable"
import {
    Order,
    PaginationObj,
    SortingObj,
    TableHeaderData
} from "./investorOverview/types"
import { ClientStatementReportsList } from "../redux-store/types/onBoarding"
import dayjs from "../utils/dayjs"

const tableHeaderData: TableHeaderData[] = [
    {
        id: 'clientName',
        label: 'Client Name',
        align: 'left',
        isSortable: true,
    },
    {
        id: 'consolidatedLevel',
        label: 'Filter Type',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'reportDate',
        label: 'Report Date',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'createdAt',
        label: 'Initiated Date',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'status',
        label: 'Status',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'action',
        label: '',
        align: 'center',
        isSortable: false,
    }
]

const statusColorMap: { [key: string]: string } = {
    pending: "warning.500",
    success: "success.800",
    failed: "error.500"
}

function DataTable({
    tableBodyData,
    handleReportDownload,
    isDownloading,
    selectedJobIdForDownload
}: {
    tableBodyData: Array<ClientStatementReportsList>;
    handleReportDownload: (jobId: string, clientName: string) => void;
    isDownloading: boolean;
    selectedJobIdForDownload: string | null;
}): JSX.Element {

    const getFilterType = (consolidatedLevel: string) => {
        if (consolidatedLevel === "PAN") {
            return "CLIENT"
        }
        return consolidatedLevel
    }


    return (
        <>
            {
                tableBodyData.map((obj: ClientStatementReportsList, index: number) => {
                    return (
                        <TableRow
                            key={`${obj.id}-${index}`}
                            sx={(theme: Theme) => ({
                                height: "4.5em",
                                '& td': {
                                    padding: "5px",
                                    height: "4.5em",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                },
                                '& td:first-of-type': {
                                    paddingLeft: "15px"
                                },
                            })}
                        >
                            <TableCell align="left">
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    })}
                                >
                                    {`${obj?.clientName ?? ""}`?.length ? obj?.clientName : "-"} {`${obj?.clientCode ?? ""}`?.length ? "(" + obj?.clientCode + ")" : "(-)"} {`${obj?.investorType ? "(" + obj?.investorType + ")" : ""}`} {`${`${obj?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""}`} {`${obj?.accountType ? " - " + obj.accountType : ""}`}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    })}
                                >
                                    {getFilterType(obj?.consolidatedLevel ?? "-")}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    })}
                                >
                                    {
                                        `${obj?.reportDate ?? ""}`?.length ?
                                            dayjs(obj?.reportDate).format('DD/MM/YYYY') : "-"
                                    }
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    })}
                                >
                                    {
                                        `${obj?.createdAt ?? ""}`?.length ?
                                            dayjs(obj?.createdAt).format('DD/MM/YYYY HH:mm:ss') : "-"
                                    }
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: statusColorMap[`${obj?.status ?? ""}`?.toLowerCase()]
                                    }}
                                >
                                    {obj?.status ?? ""}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                {(`${obj?.status ?? ""}`?.toLowerCase() === "failed") && (
                                    <Tooltip
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    margin: "0px !important",
                                                    backgroundColor: "transparent",
                                                }
                                            }
                                        }}
                                        title={
                                            <Box sx={(theme: Theme) => ({
                                                borderRadius: '10px',
                                                backgroundColor: theme.palette.customColors.cinder,
                                                padding: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                rowGap: '10px',
                                            })}>
                                                <Typography variant='subtitle_600' sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary
                                                })}>
                                                    Failed to generate Report. Please contact Admin.
                                                </Typography>
                                            </Box>
                                        }
                                    >
                                        <IconButton>
                                            <InfoIconOutlined />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(`${obj?.status ?? ""}`?.toLowerCase() === "success") && (
                                    (isDownloading && selectedJobIdForDownload === obj.jobId) ? (
                                        <CircularProgress
                                            color="info"
                                            size={20}
                                        />
                                    ) : (
                                        <IconButton
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleReportDownload(obj.jobId, obj.clientName)}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    )
                                )}
                            </TableCell>
                        </TableRow >
                    )
                })
            }
        </>
    )
}

interface ClientStatementTableProps {
    isLoading: boolean;
    tableData: Array<ClientStatementReportsList>;
    handleTableSort: (orderBy: string, order: Order) => void;
    tablePaginationObj: PaginationObj;
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    tableSortingObj: SortingObj;
    handleReportDownload: (jobId: string, clientName: string) => void;
    isDownloading: boolean;
    selectedJobIdForDownload: string | null;
}


export default function ClientStatementTable({
    isLoading,
    tableData,
    handleTableSort,
    tablePaginationObj,
    handleTableRowsPerPageChange,
    handleTablePageChange,
    tableSortingObj,
    handleReportDownload,
    isDownloading,
    selectedJobIdForDownload
}: ClientStatementTableProps): JSX.Element {
    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableBodyData={tableData}
                    handleReportDownload={handleReportDownload}
                    isDownloading={isDownloading}
                    selectedJobIdForDownload={selectedJobIdForDownload}
                />
            }
            tableData={tableData}
            tableHeaderData={tableHeaderData}
            handleSort={handleTableSort}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "15px"
                }
            }}

            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={tablePaginationObj.totalRows}
            rowsPerPage={tablePaginationObj.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tablePaginationObj.currentPage}
            order={tableSortingObj.order}
            orderBy={tableSortingObj.orderBy}
        />
    )
}